import { Injectable } from '@angular/core';
import { BaseNotifier } from '../notifiers/base-notifier';
import { Ref } from '../providers/ref';
import { AdminMenuRepositoryProvider } from '../providers/repository-providers';
import { AdminMenuEntity } from '@gishub/domain';
import { RoleMenusState } from './role-menus-state';

/**
 * RoleMenusNotifier — permisos de navegación del rol (singleton de sesión).
 *
 * Espeja el contrato del backend: el panel es accesible únicamente para las
 * rutas cubiertas por el árbol de menús del rol (GET /account/roles/menus),
 * no por el nombre del rol. La carga es single-flight y se cachea por sesión;
 * si el backend no responde (rol sin menús, error de red) `loaded` sigue en
 * false y se reintenta en la siguiente navegación.
 */
@Injectable({ providedIn: 'root' })
export class RoleMenusNotifier extends BaseNotifier<RoleMenusState> {
  private static readonly INITIAL: RoleMenusState = { routes: [], loaded: false };
  private static readonly PANEL_ROOT = '/panel';

  private inFlight: Promise<void> | null = null;

  constructor(ref: Ref) {
    super(ref, RoleMenusNotifier.INITIAL);
  }

  /** Carga los menús del rol una sola vez por sesión (single-flight). */
  async ensureLoaded(): Promise<void> {
    if (this.inFlight) return this.inFlight;
    if (this.snapshot().loaded) return;
    this.inFlight = this.load().finally(() => {
      this.inFlight = null;
    });
    return this.inFlight;
  }

  hasAnyAccess(): boolean {
    return this.snapshot().routes.length > 0;
  }

  /** ¿El rol puede navegar `url`? Comparación contra las rutas del árbol. */
  canAccess(url: string): boolean {
    const path = RoleMenusNotifier.normalizePath(url);
    if (path !== RoleMenusNotifier.PANEL_ROOT && !path.startsWith(`${RoleMenusNotifier.PANEL_ROOT}/`)) {
      return true;
    }
    const routes = this.snapshot().routes;
    if (path === RoleMenusNotifier.PANEL_ROOT) return routes.length > 0;
    // coincidencia exacta, descendientes (/:id, sub-rutas) y ancestros (grupos intermedios)
    return routes.some((r) => path === r || path.startsWith(`${r}/`) || r.startsWith(`${path}/`));
  }

  /** Limpia la caché (cierre de sesión). */
  reset(): void {
    this.inFlight = null;
    this.setState(RoleMenusNotifier.INITIAL);
  }

  private async load(): Promise<void> {
    const res = await this.ref.read(AdminMenuRepositoryProvider).instance.getItems();
    if (!res.success || !res.data) return;
    const routes = RoleMenusNotifier.collectRoutes(res.data);
    this.updateState((s) => ({ ...s, routes, loaded: true }));
  }

  private static collectRoutes(items: AdminMenuEntity[]): string[] {
    const routes: string[] = [];
    for (const item of items) {
      if (item.route) routes.push(RoleMenusNotifier.normalizePath(item.route));
      if (item.children.length) routes.push(...RoleMenusNotifier.collectRoutes(item.children));
    }
    return Array.from(new Set(routes));
  }

  private static normalizePath(value: string): string {
    const path = value.split('?')[0].split('#')[0].trim().toLowerCase();
    return path.length > 1 ? path.replace(/\/+$/, '') : path;
  }
}
